import type { EmailVerifyResponse, StationSummary } from "@station-keeping/contracts";
import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { session, useSession } from "../game/session";
import { formatClock, formatDuration, formatNumber, formatPermyriad, t } from "../i18n";
import { planetArt, starsArt } from "../render/sprites";
import { PLANET_HEIGHT } from "../render/textures";
import { apiErrorLabel } from "./labels";
import { Icon, PixelText } from "./pixel";
import { Field, Key, Led } from "./primitives";
import { ModuleThumb, ShipThumb } from "./thumbs";

type Choice = Extract<EmailVerifyResponse, { outcome: "choice_required" }>;
type Keep = "current" | "account";

type Step =
  | { kind: "email" }
  | { kind: "code"; email: string; resendAt: number }
  | { kind: "choice"; choice: Choice; keep: Keep | null };

/** Re-rendu chaque seconde tant que `active` (compte à rebours du renvoi de code). */
function useSecondTick(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

interface Planet {
  url: string;
  width: number;
}

/** Pixels CSS par pixel d'art (`--art`) : un nombre entier de pixels d'écran. */
function artPixel(): number {
  const ratio = window.devicePixelRatio || 1;
  return Math.max(1, Math.round(ratio)) / ratio;
}

/** La planète à la largeur de l'écran (par paliers de 128 pixels d'art) : aucun bord coupé. */
function drawPlanet(): Planet {
  const width = Math.ceil(window.innerWidth / artPixel() / 128) * 128;
  return { url: planetArt("night", width, PLANET_HEIGHT).toCanvas().toDataURL("image/png"), width };
}

/** Le décor de l'écran d'accueil : ciel étoilé et planète, dessinés par le même code que le plan. */
function useBackdrop(): { stars: string; planet: Planet } {
  const stars = useMemo(() => starsArt(256).toCanvas().toDataURL("image/png"), []);
  const [planet, setPlanet] = useState(drawPlanet);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const width = Math.ceil(window.innerWidth / artPixel() / 128) * 128;
        setPlanet((current) => (width > current.width ? drawPlanet() : current));
      }, 150);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(timer);
    };
  }, []);
  return { stars, planet };
}

/**
 * L'écran d'accueil de la borne : un compte est obligatoire pour jouer. On se connecte (ou on crée son
 * compte, c'est le même geste) avec son e-mail et un code à 6 chiffres. Un ancien profil invité y sécurise
 * sa station ; si son adresse a déjà un compte avec sa propre station, il choisit celle qu'il garde.
 */
export function SignInScreen() {
  const player = useSession((state) => state.player);
  const emailLoginEnabled = useSession((state) => state.config?.settings.emailLoginEnabled ?? true);
  const sessionExpired = useSession((state) => state.sessionExpired);
  const loginLink = useSession((state) => state.loginLink);
  const [step, setStep] = useState<Step>({ kind: "email" });
  const [email, setEmail] = useState(() => session.lastAccountEmail());
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const now = useSecondTick(step.kind === "code");
  const emailId = useId();
  const codeId = useId();
  const errorId = useId();
  const backdrop = useBackdrop();
  const legacyGuest = player !== null && player.email === null;

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (failure) {
      setError(apiErrorLabel(failure));
    } finally {
      setBusy(false);
    }
  };

  const goTo = (next: Step) => {
    setError(null);
    setStep(next);
  };

  const requestCode = (address: string) =>
    run(async () => {
      const sent = await session.requestCode(address);
      setCode("");
      setStep({ kind: "code", email: address, resendAt: Date.now() + sent.resendAfterSeconds * 1000 });
    });

  // La réussite rouvre la station : l'écran laisse place au jeu.
  const verify = (address: string, value: string) =>
    run(async () => {
      const result = await session.verifyCode(address, value);
      if (result.outcome === "choice_required") setStep({ kind: "choice", choice: result, keep: null });
    });

  const choose = (choice: Choice, keep: Keep) =>
    run(async () => {
      try {
        await session.chooseStation(choice.choiceToken, keep);
      } catch (failure) {
        setStep({ kind: "email" });
        throw failure;
      }
    });

  // Lien de l'e-mail : l'adresse et le code arrivent remplis, la vérification part d'elle-même.
  useEffect(() => {
    if (!loginLink) return;
    session.consumeLoginLink();
    setEmail(loginLink.email);
    setCode(loginLink.code);
    setStep({ kind: "code", email: loginLink.email, resendAt: 0 });
    void verify(loginLink.email, loginLink.code);
    // Seule l'arrivée d'un lien compte (`verify` change à chaque rendu).
  }, [loginLink]);

  const onEmail = (event: FormEvent) => {
    event.preventDefault();
    const address = email.trim().toLowerCase();
    setEmail(address);
    void requestCode(address);
  };

  const onCodeChange = (value: string, address: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    // Six chiffres : on vérifie sans attendre la touche (collage depuis l'e-mail).
    if (digits.length === 6 && !busy) void verify(address, digits);
  };

  return (
    <main
      className="relative h-full overflow-y-auto overscroll-contain"
      style={{ backgroundImage: `url(${backdrop.stars})`, backgroundSize: "calc(var(--art) * 256)", imageRendering: "pixelated" }}
    >
      <img
        src={backdrop.planet.url}
        alt=""
        aria-hidden="true"
        className="sprite pointer-events-none fixed bottom-0"
        style={{
          width: `calc(var(--art) * ${backdrop.planet.width})`,
          height: `calc(var(--art) * ${PLANET_HEIGHT})`,
          left: `calc(50% - var(--art) * ${backdrop.planet.width / 2})`,
          maxWidth: "none",
        }}
      />
      {/* L'écran de la borne : de fines lignes de balayage sur tout le décor. */}
      <div aria-hidden="true" className="crt pointer-events-none fixed inset-0" />
      <div className="relative mx-auto flex min-h-full w-full max-w-[27rem] flex-col justify-center px-4 py-8">
        <div className="mb-5 flex flex-col items-center text-center">
          <div className="flex items-end gap-2" aria-hidden="true">
            <ShipThumb shipClass="freighter" className="mb-14" />
            <ModuleThumb type="core" size="lg" />
            <ModuleThumb type="dock" className="mb-2" />
          </div>
          {/* Le fronton de la borne : le nom du jeu allumé dans son cadre. */}
          <div className="bezel mt-4 w-full">
            <h1 className="flex flex-col items-center gap-2 py-3 text-accent">
              <span className="sr-only">{t("app.title")}</span>
              <PixelText text="STATION" scale={3} label={false} />
              <PixelText text="KEEPING" scale={3} label={false} className="text-ink" />
            </h1>
          </div>
          <p className="mt-4 text-lg text-muted">{t("signin.tagline")}</p>
        </div>

        <section className="bezel" aria-labelledby={`${emailId}-title`}>
          <div className="px-2 pb-2 pt-1">
            <h2 id={`${emailId}-title`} className="dotted flex items-center gap-2 pb-3 pt-1 text-ink">
              <Led on blink color="var(--color-accent)" />
              <PixelText text={step.kind === "code" ? t("signin.codeTitle") : step.kind === "choice" ? t("account.choiceTitleShort") : t("signin.title")} />
            </h2>

            {sessionExpired && step.kind === "email" ? <Notice tone="warn">{t("account.expired")}</Notice> : null}
            {legacyGuest && step.kind === "email" ? <Notice tone="accent">{t("signin.guest")}</Notice> : null}

            {!emailLoginEnabled ? (
              <p className="mt-3 text-[0.9375rem] text-warn">{t("account.closed")}</p>
            ) : step.kind === "email" ? (
              <form onSubmit={onEmail} className="mt-3 flex flex-col gap-2">
                <label htmlFor={emailId} className="text-[0.9375rem] font-semibold text-ink">
                  {t("account.email")}
                </label>
                <Field
                  id={emailId}
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  maxLength={254}
                  autoFocus
                  value={email}
                  placeholder={t("account.emailPlaceholder")}
                  onChange={(event) => setEmail(event.target.value)}
                  aria-describedby={error ? errorId : `${emailId}-hint`}
                />
                <p id={`${emailId}-hint`} className="text-[0.875rem] text-muted">
                  {t("signin.hint")}
                </p>
                <Key type="submit" tone="action" disabled={busy} className="mt-2 w-full">
                  <Icon name="mail" />
                  {busy ? t("signin.sending") : t("account.sendCode")}
                </Key>
              </form>
            ) : step.kind === "code" ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void verify(step.email, code);
                }}
                className="mt-3 flex flex-col gap-2"
              >
                <p className="text-[0.9375rem] text-muted">{t("account.codeSent", { email: step.email })}</p>
                <label htmlFor={codeId} className="mt-1 text-[0.9375rem] font-semibold text-ink">
                  {t("account.code")}
                </label>
                <Field
                  id={codeId}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(event) => onCodeChange(event.target.value, step.email)}
                  className="tabular text-center text-[1.75rem] font-semibold tracking-[0.5em]"
                  aria-describedby={error ? errorId : undefined}
                />
                <Key type="submit" tone="action" disabled={busy || code.length !== 6} className="mt-2 w-full">
                  {busy ? t("signin.checking") : t("account.verify")}
                </Key>
                <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                  <Key tone="quiet" size="sm" onClick={() => goTo({ kind: "email" })} disabled={busy}>
                    {t("account.changeEmail")}
                  </Key>
                  {step.resendAt > now ? (
                    <span className="flex items-center gap-2 text-[0.875rem] text-dim">
                      {t("signin.resendIn")}
                      <PixelText text={formatClock((step.resendAt - now) / 1000)} label={formatDuration((step.resendAt - now) / 1000)} />
                    </span>
                  ) : (
                    <Key tone="quiet" size="sm" onClick={() => void requestCode(step.email)} disabled={busy}>
                      {t("account.resend")}
                    </Key>
                  )}
                </div>
              </form>
            ) : (
              <div className="mt-3 flex flex-col gap-2">
                <p className="text-[0.9375rem] text-muted">{t("account.choiceBody")}</p>
                <div className="mt-1 grid gap-2" role="radiogroup" aria-label={t("account.choiceTitle")}>
                  <StationOption
                    title={t("account.choiceCurrent")}
                    summary={step.choice.current}
                    selected={step.keep === "current"}
                    onSelect={() => setStep({ ...step, keep: "current" })}
                  />
                  <StationOption
                    title={t("account.choiceAccount")}
                    summary={step.choice.account}
                    selected={step.keep === "account"}
                    onSelect={() => setStep({ ...step, keep: "account" })}
                  />
                </div>
                {step.keep ? (
                  <>
                    <p className="mt-1 text-[0.9375rem] text-stop">{t("account.keepWarning")}</p>
                    <Key tone="danger" disabled={busy} onClick={() => step.keep && void choose(step.choice, step.keep)}>
                      {t("account.keepConfirm")}
                    </Key>
                  </>
                ) : null}
                <Key tone="quiet" size="sm" onClick={() => goTo({ kind: "email" })} disabled={busy}>
                  {t("account.cancel")}
                </Key>
              </div>
            )}

            {error ? (
              <p id={errorId} role="alert" className="mt-3 text-[0.9375rem] text-stop">
                {error}
              </p>
            ) : null}
          </div>
        </section>
        <p className="mt-5 text-center text-[0.875rem] text-dim">{t("signin.studio")}</p>
      </div>
    </main>
  );
}

function Notice({ tone, children }: { tone: "warn" | "accent"; children: string }) {
  const color = tone === "warn" ? "var(--color-warn)" : "var(--color-accent)";
  return (
    <p className="well mt-3 flex items-start gap-3 px-3 py-2.5 text-[0.9375rem] text-ink">
      <Led on color={color} className="mt-1.5" />
      {children}
    </p>
  );
}

function StationOption({
  title,
  summary,
  selected,
  onSelect,
}: {
  title: string;
  summary: StationSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  const stats = t("account.choiceStats", {
    credits: formatNumber(summary.credits),
    reputation: formatPermyriad(summary.reputation),
    modules: summary.modules,
  });
  const age = t("account.choiceAge", { time: formatDuration(summary.tick) });
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={`${title} : ${stats}, ${age}`}
      onClick={onSelect}
      className="key w-full flex-col items-start normal-case tracking-normal"
    >
      <span className="flex w-full items-center gap-2 text-base font-semibold text-ink">
        <Led on={selected} color="var(--color-accent)" />
        {title}
      </span>
      <span className="tabular block text-[0.9375rem] font-normal text-muted">{stats}</span>
      <span className="tabular block text-[0.875rem] font-normal text-dim">{age}</span>
    </button>
  );
}
